import { AxiosInstance } from 'axios'
import { StringStorage } from 'src/lib/StringStorage'
import { UserService } from 'src/auth/user/UserService'
import { createStore, storeFromObservable } from '@core'
import { ReadOnlyStore, Store } from '@core/types'
import { UserApi } from 'src/auth/user/UserApi'
import { ApiClient } from 'src/api/client/ApiClient'
import { ClientUser } from 'src/api/defs/ClientUser.type'
import { flatMap, map } from 'rxjs/operators'
import { HttpRequestInterceptor } from 'src/http/types'
import { StoreDebugger } from 'src/lib/StoreDebugger'
import { of } from 'rxjs'
import { setInfoMessages } from '../context/InfoMessageContext'

type Res = {
    service: UserService
    userStore: Store<ClientUser | null>
    requestInterceptor: HttpRequestInterceptor
    onHttpError: (res: { status: number; data: any }) => void
    loadTokenFromStorage: () => void
    sessionStore: ReadOnlyStore<boolean | null>
}

const RX_PRIMARY_API = /^\/api(\/|$)/
const RX_AUTH = /^\/auth(\/|$)/

const excludeFromEnv: RegExp[] = (process.env.SPA_BEARER_EXCLUDE || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(s => new RegExp(`^${s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`))

const NO_AUTH_PATTERNS: RegExp[] = [RX_AUTH, ...excludeFromEnv, /^\/api\/info$/]

const shouldSkipAuth = (url: string, skipAuthFlag?: boolean) => {
    if (skipAuthFlag) return true
    if (!RX_PRIMARY_API.test(url)) return true
    return NO_AUTH_PATTERNS.some(rx => rx.test(url))
}

export const setupAuth = (
    http: AxiosInstance,
    api: ApiClient,
    dbgr: StoreDebugger
): Res => {
    const tokenStorage = new StringStorage('auth/token')

    const userApi = new UserApi(api)

    const userStore = createStore<ClientUser | null>(null)

    // Connect token and user stores
    tokenStorage
        .changes()
        .pipe(
            flatMap(token =>
                token
                    ? userApi
                          .current()
                          .then(user => {
                              if (user?.infomessages) {
                                  setInfoMessages(user.infomessages)
                              }
                              return user
                          })
                          .catch(err => {
                              // Prevent unsubscription - this can happen for multitude of reasons (one of them is stale token)
                              // Unsubsribing would disable this pipe if user tries again
                              console.log('Error getting current user', err)
                              return null
                          })
                    : of(null)
            )
        )
        .subscribe(userStore.change)

    const sessionStore = storeFromObservable(
        null,
        tokenStorage.changes().pipe(map(t => !!t))
    )

    if (dbgr) {
        dbgr.addStore('token', tokenStorage)
    }

    return {
        service: new UserService(http, tokenStorage, userStore),
        userStore,
        requestInterceptor: req => {
            const token = tokenStorage.value()
            const rawUrl = req?.url || ''
            let path = rawUrl
            try {
                path = new URL(rawUrl, window.location.origin).pathname
            } catch {}

            const skip = shouldSkipAuth(path, req.skipAuth)
            const isAuth = /^\/auth(\/|$)/.test(path)

            if (token && !skip && !isAuth) {
                req.headers = {
                    ...req.headers,
                    Authorization: `Bearer ${token}`
                }
            }
            return req
        },
        onHttpError: err => {
            // TODO check the code as well !!
            if (err.status === 401) {
                console.log('Token expired. Logging out.')
                tokenStorage.clear()
            }
        },
        loadTokenFromStorage: () => {
            tokenStorage.load()
        },
        sessionStore
    }
}
